import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";
import { useNavigate } from "react-router-dom";

const formatDate = (d) => {
  if (!d) return "N/A";
  return new Date(d).toLocaleString("en-IN");
};

const safeLower = (v) => String(v || "").toLowerCase();

const roleText = (u) => {
  // supports: role / userType / type
  return u?.role || u?.userType || u?.type || "N/A";
};

/** ✅ Supports both adminToken + token (+ optional auth.token) */
const getAnyToken = () => {
  const adminToken = localStorage.getItem("adminToken");
  const token = localStorage.getItem("token");

  let authToken = null;
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    authToken = auth?.token || null;
  } catch {}

  return adminToken || token || authToken || "";
};

const AllUsers = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [viewCard, setViewCard] = useState(false);

  const [confirmBlock, setConfirmBlock] = useState(null);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 8;

  // ✅ read token once on mount
  const token = useMemo(() => getAnyToken(), []);

  const authHeaders = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  // ✅ Fetch all users
  useEffect(() => {
    let isMounted = true;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError("");

        if (!token) {
          setError("Not logged in. Please login again.");
          navigate("/admin/login");
          return;
        }

        const res = await axios.get(`${baseurl}/api/admin/user/all-users`, {
          headers: authHeaders,
          withCredentials: true,
        });

        if (!isMounted) return;

        const data = Array.isArray(res.data) ? res.data : res.data?.users || [];
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching users:", err);

        if (err?.response?.status === 401) {
          setError("Session expired. Please login again.");
          navigate("/admin/login");
          return;
        }

        setError(err.response?.data?.message || "Failed to load users");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchUsers();

    return () => {
      isMounted = false;
    };
  }, [navigate, token, authHeaders]);

  // ✅ Filtered users
  const filteredUsers = useMemo(() => {
    const q = safeLower(searchQuery.trim());
    if (!q) return users;

    return users.filter((u) => {
      return (
        safeLower(u._id).includes(q) ||
        safeLower(u.name).includes(q) ||
        safeLower(u.fullName).includes(q) ||
        safeLower(u.email).includes(q) ||
        safeLower(u.mobile).includes(q) ||
        safeLower(roleText(u)).includes(q)
      );
    });
  }, [users, searchQuery]);

  // ✅ New users today (local time)
  const newUsersCount = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const todayLocal = `${y}-${m}-${d}`;

    return users.filter((u) => String(u.createdAt || "").startsWith(todayLocal)).length;
  }, [users]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));
  const currentUsers = useMemo(() => {
    const start = (currentPage - 1) * usersPerPage;
    return filteredUsers.slice(start, start + usersPerPage);
  }, [filteredUsers, currentPage]);

  useEffect(() => setCurrentPage(1), [searchQuery, users.length]);

  // ✅ Delete user
  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this user permanently?");
    if (!ok) return;

    try {
      setActionLoading(true);

      await axios.delete(`${baseurl}/api/admin/user/delete/${id}`, {
        headers: authHeaders,
        withCredentials: true,
      });

      setUsers((prev) => prev.filter((u) => u._id !== id));
      // close modals if deleted user is open
      if (selectedUser?._id === id) {
        setViewCard(false);
        setSelectedUser(null);
        setOrders([]);
      }
    } catch (err) {
      console.error("Error deleting user:", err);

      if (err?.response?.status === 401) {
        alert("Session expired. Please login again.");
        navigate("/admin/login");
        return;
      }

      alert(err.response?.data?.message || "Delete failed");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ Block / Unblock
  const handleBlock = async (id, isBlocked) => {
    try {
      setActionLoading(true);

      const endpoint = isBlocked
        ? `${baseurl}/api/admin/user/unblock/${id}`
        : `${baseurl}/api/admin/user/block/${id}`;

      await axios.put(endpoint, {}, { headers: authHeaders, withCredentials: true });

      setUsers((prev) =>
        prev.map((u) => (u._id === id ? { ...u, isBlocked: !isBlocked } : u))
      );

      // keep selectedUser in sync if modal open
      if (selectedUser?._id === id) {
        setSelectedUser((prev) => (prev ? { ...prev, isBlocked: !isBlocked } : prev));
      }

      setConfirmBlock(null);
    } catch (err) {
      console.error("Error blocking/unblocking:", err);

      if (err?.response?.status === 401) {
        alert("Session expired. Please login again.");
        navigate("/admin/login");
        return;
      }

      alert(err.response?.data?.message || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ View full user info + orders
  const handleViewDetails = async (userId) => {
    try {
      setActionLoading(true);

      // 1) Full user details from admin API
      const userRes = await axios.get(`${baseurl}/api/admin/user/details/${userId}`, {
        headers: authHeaders,
        withCredentials: true,
      });

      const userData = userRes.data?.user || userRes.data;

      // 2) Orders (best-effort)
      let orderData = [];
      try {
        const orderRes = await axios.get(`${baseurl}/api/order/get-user-order/${userId}`, {
          headers: authHeaders,
          withCredentials: true,
        });

        orderData = Array.isArray(orderRes.data)
          ? orderRes.data
          : Array.isArray(orderRes.data?.orders)
          ? orderRes.data.orders
          : orderRes.data
          ? [orderRes.data]
          : [];
      } catch {
        orderData = [];
      }

      setSelectedUser(userData);
      setOrders(orderData);
      setViewCard(true);
    } catch (err) {
      console.error("Error fetching user details:", err);

      if (err?.response?.status === 401) {
        alert("Session expired. Please login again.");
        navigate("/admin/login");
        return;
      }

      alert(err.response?.data?.message || "Failed to fetch user details");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ ESC closes modals
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setConfirmBlock(null);
        setViewCard(false);
      }
    };
    if (confirmBlock || viewCard) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirmBlock, viewCard]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">All Users</h1>
            <p className="text-sm text-gray-500 mt-1">
              Search users, view full info, block/unblock, delete.
            </p>
          </div>

          <div className="w-full sm:w-[360px]">
            <input
              type="text"
              placeholder="Search by ID / Name / Email / Mobile / Role"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="text-xs text-gray-500">New Users Today</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{newUsersCount}</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="text-xs text-gray-500">Total Users</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{users.length}</div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="text-xs text-gray-500">Showing</div>
            <div className="text-2xl font-bold text-gray-800 mt-1">
              {filteredUsers.length}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">User</th>
                <th className="px-4 py-3 text-left font-semibold">Email / Mobile</th>
                <th className="px-4 py-3 text-left font-semibold">Role</th>
                <th className="px-4 py-3 text-left font-semibold">Created</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                    Loading users...
                  </td>
                </tr>
              ) : currentUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                currentUsers.map((user) => (
                  <tr key={user._id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">
                        {user.name || user.fullName || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">{user._id}</div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-gray-800">{user.email || "N/A"}</div>
                      <div className="text-xs text-gray-500">{user.mobile || "N/A"}</div>
                    </td>

                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs font-semibold">
                        {roleText(user)}
                      </span>
                    </td>

                    <td className="px-4 py-3 text-gray-600">{formatDate(user.createdAt)}</td>

                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          user.isBlocked
                            ? "bg-red-100 text-red-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {user.isBlocked ? "Blocked" : "Active"}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleViewDetails(user._id)}
                          disabled={actionLoading}
                          className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          View
                        </button>

                        <button
                          onClick={() => setConfirmBlock(user)}
                          disabled={actionLoading}
                          className={`px-3 py-1 rounded text-white disabled:opacity-50 ${
                            user.isBlocked
                              ? "bg-yellow-500 hover:bg-yellow-600"
                              : "bg-indigo-600 hover:bg-indigo-700"
                          }`}
                        >
                          {user.isBlocked ? "Unblock" : "Block"}
                        </button>

                        <button
                          onClick={() => handleDelete(user._id)}
                          disabled={actionLoading}
                          className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && filteredUsers.length > 0 && (
          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <div className="text-sm text-gray-600">
              Page <b>{currentPage}</b> of <b>{totalPages}</b>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Prev
              </button>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Block/Unblock Modal */}
      {confirmBlock && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={() => setConfirmBlock(null)}
        >
          <div
            className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-900">
                {confirmBlock.isBlocked ? "Unblock User" : "Block User"}
              </h3>
              <button
                className="px-2 py-1 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setConfirmBlock(null)}
              >
                Close
              </button>
            </div>

            <div className="p-4 text-sm text-gray-700">
              Are you sure you want to{" "}
              <b>{confirmBlock.isBlocked ? "unblock" : "block"}</b>{" "}
              <span className="font-semibold">
                {confirmBlock.name || confirmBlock.fullName || "this user"}
              </span>
              ?
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setConfirmBlock(null)}
              >
                Cancel
              </button>

              <button
                disabled={actionLoading}
                className={`px-4 py-2 rounded text-white disabled:opacity-50 ${
                  confirmBlock.isBlocked
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                onClick={() => handleBlock(confirmBlock._id, confirmBlock.isBlocked)}
              >
                Yes, {confirmBlock.isBlocked ? "Unblock" : "Block"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View User Modal */}
      {viewCard && selectedUser && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={() => setViewCard(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-900">User Details</h3>

              <button
                className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setViewCard(false)}
              >
                Close
              </button>
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <InfoCard label="Name" value={selectedUser.name || selectedUser.fullName || "N/A"} />
              <InfoCard label="User ID" value={selectedUser._id || "N/A"} mono />
              <InfoCard label="Role" value={roleText(selectedUser)} />
              <InfoCard label="Email" value={selectedUser.email || "N/A"} />
              <InfoCard label="Mobile" value={selectedUser.mobile || "N/A"} />
              <InfoCard label="Verified" value={selectedUser.isVerified ? "Yes" : "No"} />
              <InfoCard
                label="Status"
                value={selectedUser.isBlocked ? "Blocked" : "Active"}
                statusColor={selectedUser.isBlocked ? "text-red-700" : "text-green-700"}
              />
              <InfoCard label="Created At" value={formatDate(selectedUser.createdAt)} />
            </div>

            <div className="p-4 border-t">
              <h4 className="font-semibold text-gray-900 mb-3">Orders</h4>

              {orders.length === 0 ? (
                <div className="text-gray-500 text-sm">No orders found.</div>
              ) : (
                <div className="space-y-2">
                  {orders.map((o, i) => (
                    <div key={o._id || i} className="border rounded p-3 bg-gray-50">
                      <div className="flex justify-between gap-4 flex-wrap">
                        <div>
                          <div className="text-xs text-gray-500">Order ID</div>
                          <div className="font-mono text-gray-900">{o.orderId || o._id || "N/A"}</div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Status</div>
                          <div className="font-semibold text-gray-900">
                            {o.status || o.latestStatus?.status || "N/A"}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Total</div>
                          <div className="font-semibold text-gray-900">
                            ₹{Number(o.totalAmount || o.amount || 0).toFixed(2)}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-500">Created</div>
                          <div className="text-gray-700">{formatDate(o.createdAt)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end">
              <button
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setViewCard(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoCard = ({ label, value, mono, statusColor }) => (
  <div className="bg-gray-50 border rounded p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div
      className={`mt-1 ${mono ? "font-mono" : "font-semibold"} ${
        statusColor ? statusColor : "text-gray-900"
      }`}
    >
      {value}
    </div>
  </div>
);

export default AllUsers;













































// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { baseurl } from "../../config/url";

// const AllUsers = () => {
//   const [users, setUsers] = useState([]);
//   const [filteredUsers, setFilteredUsers] = useState([]);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [orders, setOrders] = useState([]);
//   const [viewCard, setViewCard] = useState(false);
//   const [confirmBlock, setConfirmBlock] = useState(null);
//   const [newUsersCount, setNewUsersCount] = useState(0);

//   // Pagination
//   const [currentPage, setCurrentPage] = useState(1);
//   const usersPerPage = 5;

//   // Fetch all users
//   useEffect(() => {
//     const fetchUsers = async () => {
//       try {
//         const token = localStorage.getItem("token");
//         const res = await axios.get(`${baseurl}/api/admin/user/all-users`, {
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         const data = Array.isArray(res.data) ? res.data : res.data.users || [];
//         setUsers(data);
//         setFilteredUsers(data);

//         const today = new Date().toISOString().split("T")[0];
//         setNewUsersCount(data.filter(u => u.createdAt?.startsWith(today)).length);
//       } catch (err) {
//         console.error("Error fetching users:", err);
//       }
//     };

//     fetchUsers();
//   }, []);

//   // Search
//   const handleSearch = e => {
//     const query = e.target.value.toLowerCase();
//     setSearchQuery(query);
//     const filtered = users.filter(
//       user =>
//         user._id.toLowerCase().includes(query) ||
//         user.name.toLowerCase().includes(query) ||
//         (user.email && user.email.toLowerCase().includes(query)) ||
//         (user.mobile && user.mobile.toLowerCase().includes(query))
//     );
//     setFilteredUsers(filtered);
//     setCurrentPage(1);
//   };

//   // Delete
//   const handleDelete = async id => {
//     if (!window.confirm("Are you sure to delete this user?")) return;
//     try {
//       const token = localStorage.getItem("token");
//       await axios.delete(`${baseurl}/api/admin/user/delete/${id}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       const updated = users.filter(u => u._id !== id);
//       setUsers(updated);
//       setFilteredUsers(updated);
//       alert("User deleted successfully!");
//     } catch (err) {
//       console.error("Error deleting user:", err);
//     }
//   };

//   // Block/Unblock
//   const confirmBlockUser = user => setConfirmBlock(user);

//   const handleBlock = async (id, isBlocked) => {
//     try {
//       const token = localStorage.getItem("token");
//       const endpoint = isBlocked
//         ? `${baseurl}/api/admin/user/unblock/${id}`
//         : `${baseurl}/api/admin/user/block/${id}`;
//       await axios.put(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });

//       const updatedUsers = users.map(u => u._id === id ? { ...u, isBlocked: !isBlocked } : u);
//       setUsers(updatedUsers);
//       setFilteredUsers(updatedUsers);
//       setConfirmBlock(null);
//     } catch (err) {
//       console.error("Error blocking/unblocking:", err);
//     }
//   };

//   // View User Details
//   const handleViewDetails = async userId => {
//     try {
//       const token = localStorage.getItem("token");
//       const userRes = await axios.get(`${baseurl}/api/admin/user/details/${userId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });
//       const orderRes = await axios.get(`${baseurl}/api/order/get-user-order/${userId}`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       setSelectedUser(userRes.data);
//       setOrders(Array.isArray(orderRes.data) ? orderRes.data : [orderRes.data]);
//       setViewCard(true); // Show the modal
//     } catch (err) {
//       console.error("Error fetching user details:", err);
//       alert("Failed to fetch user details.");
//     }
//   };

//   // Pagination
//   const indexOfLastUser = currentPage * usersPerPage;
//   const indexOfFirstUser = indexOfLastUser - usersPerPage;
//   const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
//   const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

//   return (
//     <div className="p-6 bg-gray-50 min-h-screen">
//       <h1 className="text-3xl font-bold mb-6 text-center">All Users</h1>

//       {/* Search */}
//       <div className="mb-4 flex justify-center">
//         <input
//           type="text"
//           placeholder="Search by ID, Name, Email, or Mobile"
//           value={searchQuery}
//           onChange={handleSearch}
//           className="w-full max-w-md px-4 py-2 rounded shadow border focus:ring-2 focus:ring-blue-400"
//         />
//       </div>

//       {/* Summary */}
//       <div className="grid grid-cols-2 gap-4 mb-6 max-w-xl mx-auto">
//         <div className="bg-green-100 p-4 rounded shadow text-center">
//           <h3 className="text-lg font-semibold">New Users Today</h3>
//           <p className="text-2xl font-bold text-green-600">{newUsersCount}</p>
//         </div>
//         <div className="bg-blue-100 p-4 rounded shadow text-center">
//           <h3 className="text-lg font-semibold">Total Users</h3>
//           <p className="text-2xl font-bold text-blue-600">{users.length}</p>
//         </div>
//       </div>

//       {/* Users Table */}
//       <div className="overflow-x-auto">
//         <table className="min-w-full border shadow rounded-lg">
//           <thead className="bg-gray-100">
//             <tr>
//               <th className="border px-4 py-2">ID</th>
//               <th className="border px-4 py-2">Name</th>
//               <th className="border px-4 py-2">Email</th>
//               <th className="border px-4 py-2">Mobile</th>
//               <th className="border px-4 py-2">Status</th>
//               <th className="border px-4 py-2">Actions</th>
//             </tr>
//           </thead>
//           <tbody>
//             {currentUsers.map(user => (
//               <tr key={user._id} className="hover:bg-gray-50">
//                 <td className="border px-4 py-2">{user._id}</td>
//                 <td className="border px-4 py-2">{user.name}</td>
//                 <td className="border px-4 py-2">{user.email || "N/A"}</td>
//                 <td className="border px-4 py-2">{user.mobile || "N/A"}</td>
//                 <td className="border px-4 py-2">
//                   <span className={`px-2 py-1 rounded-full text-white text-sm ${user.isBlocked ? "bg-red-500" : "bg-green-500"}`}>
//                     {user.isBlocked ? "Blocked" : "Active"}
//                   </span>
//                 </td>
//                 <td className="border px-4 py-2 flex flex-wrap gap-2">
//                   <button
//                     onClick={() => handleViewDetails(user._id)}
//                     className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded shadow"
//                   >
//                     View
//                   </button>
//                   <button
//                     onClick={() => confirmBlockUser(user)}
//                     className={`px-3 py-1 rounded shadow text-white ${user.isBlocked ? "bg-yellow-500 hover:bg-yellow-600" : "bg-indigo-500 hover:bg-indigo-600"}`}
//                   >
//                     {user.isBlocked ? "Unblock" : "Block"}
//                   </button>
//                   <button
//                     onClick={() => handleDelete(user._id)}
//                     className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded shadow"
//                   >
//                     Delete
//                   </button>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Pagination */}
//       <div className="flex justify-center items-center mt-4 gap-4">
//         <button
//           disabled={currentPage === 1}
//           onClick={() => setCurrentPage(prev => prev - 1)}
//           className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
//         >
//           Prev
//         </button>
//         <span>Page {currentPage} of {totalPages}</span>
//         <button
//           disabled={currentPage === totalPages}
//           onClick={() => setCurrentPage(prev => prev + 1)}
//           className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
//         >
//           Next
//         </button>
//       </div>

//       {/* Block Modal */}
//       {confirmBlock && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
//           <div className="bg-white p-6 rounded shadow-lg w-96">
//             <h3 className="text-lg font-bold mb-4">{confirmBlock.isBlocked ? "Unblock User" : "Block User"}</h3>
//             <p className="mb-4">Are you sure you want to {confirmBlock.isBlocked ? "unblock" : "block"} <strong>{confirmBlock.name}</strong>?</p>
//             <div className="flex justify-end gap-2">
//               <button className="px-4 py-2 bg-gray-300 rounded" onClick={() => setConfirmBlock(null)}>Cancel</button>
//               <button className={`px-4 py-2 text-white rounded ${confirmBlock.isBlocked ? "bg-yellow-500" : "bg-blue-500"}`}
//                       onClick={() => handleBlock(confirmBlock._id, confirmBlock.isBlocked)}>
//                 Yes, {confirmBlock.isBlocked ? "Unblock" : "Block"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* User Detail Card */}
//       {viewCard && selectedUser && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
//           <div className="bg-white rounded-2xl shadow-xl p-6 w-[500px] max-h-[90vh] overflow-y-auto relative">
//             <button
//               className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl font-bold"
//               onClick={() => setViewCard(false)}
//             >✕</button>

//             <h2 className="text-2xl font-bold mb-4 text-center text-blue-600">User Details</h2>
//             <div className="space-y-2">
//               <p><span className="font-semibold">ID:</span> {selectedUser._id}</p>
//               <p><span className="font-semibold">Name:</span> {selectedUser.name}</p>
//               <p><span className="font-semibold">Email:</span> {selectedUser.email || "N/A"}</p>
//               <p><span className="font-semibold">Mobile:</span> {selectedUser.mobile || "N/A"}</p>
//               <p><span className="font-semibold">Status:</span> {selectedUser.isBlocked ? "Blocked" : "Active"}</p>
//               <p><span className="font-semibold">Created At:</span> {new Date(selectedUser.createdAt).toLocaleDateString()}</p>
//             </div>

//             <div className="mt-6">
//               <h3 className="text-lg font-semibold mb-2">Orders</h3>
//               {orders.length === 0 ? (
//                 <p className="text-gray-500 text-sm">No orders found.</p>
//               ) : (
//                 <ul className="space-y-2 max-h-48 overflow-y-auto pr-2">
//                   {orders.map((o, i) => (
//                     <li key={i} className="border rounded-lg p-3 bg-gray-50 shadow-sm">
//                       <p><span className="font-semibold">Order ID:</span> {o._id}</p>
//                       <p><span className="font-semibold">Status:</span> {o.status}</p>
//                       <p><span className="font-semibold">Amount:</span> ${o.amount}</p>
//                     </li>
//                   ))}
//                 </ul>
//               )}
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default AllUsers;
